import { MouseEvent, ReactNode, useContext } from 'react';
import classNames from 'classnames';

import SearchItem from 'types/SearchItem';
import SearchIcon from 'assets/icons/search.svg';
import SettingsIcon from 'assets/icons/settings.svg';
import { ConfigContext } from '@providers/ConfigProvider';
import { CartContext } from '@providers/CartProvider';
import { HolidaysContext } from '@providers/HolidaysProvider';
import Link from '@components/atoms/Link';
import ProductCard from '@templates/ProductCard';
import ProductTile from '@templates/ProductTile';
import PromotedProductTile from '@templates/PromotedProductTile';
import { useIntl } from 'utils/intl';
import getVariantThumbnail from 'utils/getVariantThumbnail';
import getNameByType from 'utils/getNameByType';
import getMappedTags from 'utils/getMappedTags';
import map from 'utils/map';
import createStyleVariables from 'utils/createStyleVariables';
import { getDaysToDelivery } from 'utils/getShipsUntilDate';
import checkIsConfigurable from 'utils/checkIsConfigurable';
import useTrack from 'hooks/useTrack';

import classes from './ProductsList.module.scss';
import messages from './ProductsList.messages';

const attributeRowHeight = 19;
const attributesMargin = 16;

export type CardType = 'SMALL' | 'MEDIUM' | 'BIG';

type Props = {
  data?: SearchItem[];
  campaignName?: string;
  className?: string;
  cardType?: CardType;
  additionalContent?: ReactNode;
};

const getIcon = (isConfigurable: boolean, campaignName?: string) => {
  if (isConfigurable) {
    return SettingsIcon;
  }

  return campaignName ? SearchIcon : undefined;
};

const ProductsList = ({
  data = [],
  className,
  campaignName,
  cardType = 'BIG',
  additionalContent,
}: Props): JSX.Element => {
  const { appUrl, shopId } = useContext(ConfigContext);
  const { onAddToCart } = useContext(CartContext);
  const { holidays } = useContext(HolidaysContext);
  const { trackLink } = useTrack();
  const intl = useIntl();

  const maxAttributesCount = Math.max(0, ...map<SearchItem, number>(data, 'attributes.length'));

  return (
    <div
      className={classNames(classes.products, className, {
        [classes.smallProducts]: ['SMALL', 'MEDIUM'].includes(cardType),
        [classes.bigProducts]: cardType === 'BIG',
      })}
      style={createStyleVariables({
        additionalHeight: `${maxAttributesCount * attributeRowHeight + attributesMargin}px`,
      })}
      data-nosnippet
    >
      {data.map(({ variant, attributes = [] }) => {
        const [currentSupplier] = variant.currentSuppliers;
        const isConfigurable = checkIsConfigurable(variant);
        const icon = getIcon(isConfigurable, campaignName);
        const witouthAddToCart = !!campaignName || isConfigurable;

        return (
          <Link
            key={variant.id}
            className={classNames(classes.productCardWrapper, {
              [classes.cardWrapper]: cardType === 'BIG',
              [classes.recommendedWrapper]: cardType === 'MEDIUM',
              [classes.promotedWrapper]: cardType === 'SMALL',
            })}
            trackEvent={
              campaignName
                ? trackLink('common', 'product_click', {
                    id: variant.id,
                    campaign_name: campaignName,
                  })
                : undefined
            }
            href={
              isConfigurable
                ? `${campaignName ? appUrl : ''}/model/${variant.product.urlSlug}/${variant.product.id}`
                : `${campaignName ? appUrl : ''}/product/${variant.urlSlug}/${variant.id}`
            }
          >
            <div className={classes.card}>
              {cardType === 'BIG' && (
                <ProductCard
                  id={variant.id}
                  name={getNameByType(variant.names, 'VARIANT_TILE', shopId)}
                  attributes={attributes}
                  thumbnail={getVariantThumbnail(variant)}
                  daysToDelivery={getDaysToDelivery(variant.shipsUntil, holidays)}
                  rating={variant.rating}
                  netPrice={variant.price}
                  grossPrice={{ amount: variant.grossPrice, currency: variant.price.currency }}
                  priceRrp={variant.priceRrp}
                  product={variant.product}
                  tags={getMappedTags(variant.variantsTags)}
                  isEol={variant.isEol}
                  isPrerelease={variant.isPrerelease}
                  isAvailable={!!variant.availability}
                  currentSupplier={currentSupplier}
                  addIcon={icon}
                  withoutCompare={witouthAddToCart}
                  withoutDivider={witouthAddToCart}
                  buttonTitle={isConfigurable ? intl.formatMessage(messages.configure) : undefined}
                  onAddToCart={
                    witouthAddToCart
                      ? undefined
                      : (e: MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          onAddToCart(variant.id, 1);
                        }
                  }
                  imageLazyLoad
                />
              )}
              {cardType === 'MEDIUM' && (
                <ProductTile
                  name={variant.name}
                  thumbnail={getVariantThumbnail(variant)}
                  tags={getMappedTags(variant.variantsTags)}
                  productTypeId={variant.product.type.id}
                  rating={variant.rating}
                  netPrice={variant.price}
                  grossPrice={{ amount: variant.grossPrice, currency: variant.price.currency }}
                  isAvailable={!!variant.availability}
                  addIcon={icon}
                  buttonTitle={isConfigurable ? intl.formatMessage(messages.configure) : undefined}
                  onAddToCart={
                    witouthAddToCart
                      ? undefined
                      : () => {
                          onAddToCart(variant.id, 1);
                        }
                  }
                />
              )}
              {cardType === 'SMALL' && (
                <PromotedProductTile
                  name={variant.name}
                  thumbnail={getVariantThumbnail(variant)}
                  tags={getMappedTags(variant.variantsTags)}
                  productTypeId={variant.product.type.id}
                  rating={variant.rating}
                  netPrice={variant.price}
                  grossPrice={{ amount: variant.grossPrice, currency: variant.price.currency }}
                />
              )}
            </div>
          </Link>
        );
      })}
      {additionalContent}
    </div>
  );
};

export default ProductsList;
